import type { FC, ReactNode } from "react";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Button from "@mui/material/Button";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import type { TranslationKey } from "@privacy/translations";
import { useTranslation } from "@privacy/translations";
import type { AssistantCommitStepKind, AssistantConversation, AssistantTurnStream } from "../../api/assistant";
import {
  assistantHealthQueryOptions,
  isBudgetExhaustedError,
  isConversationBusyError,
  isMessageRejectedError,
  isServiceUnavailableError,
} from "../../api/assistant";
import {
  ArrowDownIcon,
  CheckIcon,
  EditIcon,
  RegistrationControllerIcon3,
  SearchIcon,
  SparklesIcon,
  SystemIcon3,
} from "../../shared/icons";
import { RegistrationStatusChip } from "../../shared/RegistrationStatus";
import type { ComposerSeed } from "./AssistantComposer";
import { AssistantComposer } from "./AssistantComposer";
import { AssistantChoicesAnswerContext } from "./AssistantChoicesCard";
import type { AssistantFirstTurn } from "./navigationState";
import { AssistantDraftCard } from "./AssistantDraftCard";
import { AssistantExamples } from "./AssistantExamples";
import { AssistantRecordCard, AssistantRecordCards } from "./AssistantMarkdownComponents";
import { AssistantMessageText } from "./AssistantMessageText";
import { AssistantThinkingIndicator } from "./AssistantThinkingIndicator";
import { AssistantWelcome } from "./AssistantWelcome";
import { customScrollbar } from "../../shared/customScrollbar";
import { latestTurnReplyIndexes, transcriptSegments } from "./assistantTranscript";
import { useStickToBottom } from "./useStickToBottom";
import { uniqueKeys } from "../../shared/uniqueKeys";
import { CHAT_CONTENT_WIDTH, CHAT_TEXT } from "./assistantChatLayout";
import { useAssistantApi } from "./AssistantApiContext";

const AWAITING_COMMIT = "commit";
const MESSAGE_WIDTH = 520;
const CHOICES_TAG = "<Choices";
const SHORT_SUBJECT_WORDS = 3;

type ActiveTool = { name: string; subject?: string | null };
type WaitingLine = { label: string; Glyph: FC<{ size: number }>; pulses?: boolean };
type CommitStep = { kind: AssistantCommitStepKind; subject?: string | null };

const STEP_LABELS: Record<AssistantCommitStepKind, TranslationKey> = {
  creatingRegistration: "Assistant.Step.Creating",
  addingRecord: "Assistant.Step.Adding",
  sendingForReview: "Assistant.Step.Review",
};

const isAboutSystems = (subject?: string | null) => /^(system|partner)$/i.test(subject ?? "");

const isShortSubject = (subject?: string | null): subject is string =>
  !!subject && subject.trim().split(/\s+/).length <= SHORT_SUBJECT_WORDS;

type AssistantChatProps = {
  conversation: AssistantConversation;
  firstTurn?: AssistantFirstTurn;
  closedMessage?: string;
};

export const AssistantChat = ({ conversation, firstTurn, closedMessage }: AssistantChatProps) => {
  const { t, formatDate } = useTranslation();
  const assistantApi = useAssistantApi();
  const [pendingText, setPendingText] = useState<string | null>(null);
  const [failedText, setFailedText] = useState<string | null>(null);
  const [streamedText, setStreamedText] = useState("");
  const [activeTool, setActiveTool] = useState<ActiveTool | null>(null);
  const [steps, setSteps] = useState<Array<CommitStep>>([]);
  const [seed, setSeed] = useState<ComposerSeed | null>(null);
  const [turnStart, setTurnStart] = useState(conversation.entries.length);
  const [isCorrecting, setIsCorrecting] = useState(false);
  const seedCountRef = useRef(0);
  const queryClient = useQueryClient();
  const { scrollRef, contentRef, isAtBottom, onScroll, scrollToBottom, scrollToTopOf } = useStickToBottom();
  const draftRef = useRef<HTMLDivElement>(null);

  const turnStream: AssistantTurnStream = {
    onText: text => {
      setActiveTool(null);
      setStreamedText(previous => previous + text);
    },
    onTool: (name, subject) => setActiveTool({ name, subject }),
    onStep: (kind, subject) => setSteps(previous => [...previous, { kind, subject }]),
  };
  const endTurnStream = () => {
    setStreamedText("");
    setActiveTool(null);
    setSteps([]);
  };
  const startTurn = () => {
    endTurnStream();
    setTurnStart(conversation.entries.length);
  };

  const sendMessage = useMutation(assistantApi.sendMessageMutationOptions());
  const approveCommit = useMutation(assistantApi.approveCommitMutationOptions());

  const isWorking = sendMessage.isPending || approveCommit.isPending;
  const error = sendMessage.error ?? approveCommit.error;
  const budgetWarning = sendMessage.data?.budget ?? approveCommit.data?.budget ?? null;

  const drafts = conversation.drafts ?? [];
  const awaitsCommit = conversation.awaitingApproval === AWAITING_COMMIT;
  const showsDraftCard = drafts.length > 0 && awaitsCommit;
  const displaysDraftCard = showsDraftCard && !sendMessage.isPending;

  const send = (text: string, idempotencyKey: string = crypto.randomUUID()) => {
    scrollToBottom();
    setPendingText(text);
    setFailedText(null);
    approveCommit.reset();
    setSeed(null);
    setIsCorrecting(showsDraftCard);
    startTurn();
    sendMessage.mutate(
      { conversationId: conversation.conversationId, idempotencyKey, text, stream: turnStream },
      {
        onSettled: () => {
          setPendingText(null);
          endTurnStream();
        },
        onError: error => {
          if (isMessageRejectedError(error)) {
            seedComposer(text);
            return;
          }
          setFailedText(text);
        },
      }
    );
  };

  const seedComposer = (text: string) => {
    seedCountRef.current += 1;
    setSeed({ key: seedCountRef.current, text });
  };

  const submit = () => {
    if (isWorking) return;
    scrollToBottom();
    sendMessage.reset();
    setFailedText(null);
    startTurn();
    approveCommit.mutate(
      { conversationId: conversation.conversationId, idempotencyKey: crypto.randomUUID(), stream: turnStream },
      { onSettled: endTurnStream }
    );
  };

  const firstTurnSentRef = useRef(false);
  useEffect(() => {
    if (!firstTurn || firstTurnSentRef.current) return;
    firstTurnSentRef.current = true;
    send(firstTurn.text, firstTurn.idempotencyKey);
  });

  const turnEntries = conversation.entries.slice(turnStart);
  const replyLanded = turnEntries.some(entry => entry.kind === "message" && entry.role === "assistant");
  const pendingIsInTranscript = turnEntries.some(
    entry => entry.kind === "message" && entry.role === "user" && entry.text === pendingText
  );
  const showsPendingBubble = pendingText !== null && !pendingIsInTranscript;
  const answersChoices = !awaitsCommit && !approveCommit.isPending && (isWorking ? replyLanded : pendingText === null);
  const latestReplyIndex = answersChoices ? latestTurnReplyIndexes(conversation.entries).at(-1) : undefined;
  const latestReply = latestReplyIndex === undefined ? undefined : conversation.entries[latestReplyIndex];
  const showsChoices = latestReply?.kind === "message" && latestReply.text.includes(CHOICES_TAG);
  const isEmpty = conversation.entries.length === 0 && pendingText === null;

  const waitingLine = (): WaitingLine => {
    const thinking = { label: t("Assistant.Working"), Glyph: SparklesIcon, pulses: true };
    if (!activeTool) return thinking;
    const { name, subject } = activeTool;
    if (name === "search_records" || name === "list_records" || name === "get_record")
      return isAboutSystems(subject)
        ? { label: t("Assistant.Tool.Systems"), Glyph: SystemIcon3 }
        : { label: t("Assistant.Tool.Registrations"), Glyph: RegistrationControllerIcon3 };
    if (name === "web_search")
      return {
        label: isShortSubject(subject)
          ? t("Assistant.Tool.LookingUp", { subject: subject.trim() })
          : t("Assistant.Tool.LookingUpSupplier"),
        Glyph: SearchIcon,
      };
    if (name === "write_answers")
      return { label: t(isCorrecting ? "Assistant.Tool.Changing" : "Assistant.Tool.Drafting"), Glyph: EditIcon };
    return thinking;
  };

  const scrollToDraft = useEffectEvent(() => scrollToTopOf(draftRef.current));

  useEffect(() => {
    if (displaysDraftCard) scrollToDraft();
  }, [displaysDraftCard]);

  useEffect(() => {
    if (error !== null) void queryClient.invalidateQueries({ queryKey: assistantHealthQueryOptions().queryKey });
  }, [error, queryClient]);

  const showsSteps = approveCommit.isPending && steps.length > 0 && !replyLanded;
  const showsThinking = isWorking && !replyLanded && !showsSteps;
  const stepLabel = (step: CommitStep) => t(STEP_LABELS[step.kind], { name: step.subject ?? "" });
  const currentStep = steps.at(-1);
  const stepKeys = uniqueKeys(steps.map(step => `${step.kind}:${step.subject ?? ""}`));
  const announcement = showsSteps && currentStep ? stepLabel(currentStep) : showsThinking ? waitingLine().label : "";

  return (
    <Stack sx={{ flex: 1, minHeight: 0, ...CHAT_TEXT }}>
      <Box ref={scrollRef} onScroll={onScroll} sx={{ flex: 1, minHeight: 0, overflowY: "auto", ...customScrollbar }}>
        <Stack ref={contentRef} sx={{ gap: 3, p: 4, width: "100%", maxWidth: CHAT_CONTENT_WIDTH, mx: "auto" }}>
          <AssistantWelcome isLarge={isEmpty} shrinksIn={!!firstTurn} />
          {transcriptSegments(conversation.entries).map(segment =>
            segment.kind === "records" ? (
              <AssistantSide key={segment.key}>
                <AssistantRecordCards>
                  {segment.records.map(record => (
                    <AssistantRecordCard
                      key={record.recordId}
                      formType={record.formType}
                      recordId={record.recordId}
                      name={record.name}
                      status={t("Assistant.Draft.Created")}
                    />
                  ))}
                </AssistantRecordCards>
              </AssistantSide>
            ) : segment.entry.kind === "message" ? (
              <AssistantChoicesAnswerContext
                key={segment.key}
                value={segment.index === latestReplyIndex ? { onSend: send } : {}}
              >
                <MessageBubble role={segment.entry.role} text={segment.entry.text} />
              </AssistantChoicesAnswerContext>
            ) : segment.entry.kind === "submitted" ? (
              <ChatLine key={segment.key} status={<RegistrationStatusChip status="review" />}>
                {t("Assistant.Submitted", {
                  name: assistantApi.displayName,
                  time: segment.entry.at ? formatDate(segment.entry.at, { showTime: true }) : "",
                })}
              </ChatLine>
            ) : null
          )}
          {showsPendingBubble && <MessageBubble role="user" text={pendingText} />}

          {displaysDraftCard && (
            <Box ref={draftRef}>
              <AssistantDraftCard drafts={drafts} isSubmitting={approveCommit.isPending} onSubmit={submit} />
            </Box>
          )}

          {isWorking && !replyLanded && streamedText.length > 0 && (
            <MessageBubble role="assistant" text={streamedText} isStreaming />
          )}
          <Box role="status" aria-live="polite" sx={visuallyHidden}>
            {announcement}
          </Box>
          {showsSteps && (
            <Stack component="ol" sx={{ m: 0, p: 0, gap: 1, listStyle: "none" }}>
              {steps.map((step, index) => (
                <Stack key={stepKeys[index]} component="li" direction="row" sx={{ gap: 1 }}>
                  <Box sx={{ display: "inline-flex", width: 16, color: "primary.main", pt: "3px" }}>
                    {index < steps.length - 1 && <CheckIcon size={14} />}
                  </Box>
                  {index < steps.length - 1 ? (
                    <Typography sx={{ color: "text.secondary" }}>{stepLabel(step)}</Typography>
                  ) : (
                    <AssistantThinkingIndicator label={stepLabel(step)} />
                  )}
                </Stack>
              ))}
            </Stack>
          )}
          {showsThinking && (
            <AssistantSide>
              <AssistantThinkingIndicator {...waitingLine()} />
            </AssistantSide>
          )}
        </Stack>
      </Box>

      <Box sx={{ position: "relative", px: 4, pt: 1, pb: 3, width: "100%", maxWidth: CHAT_CONTENT_WIDTH, mx: "auto" }}>
        {!isAtBottom && (
          <IconButton
            aria-label={t("Assistant.ScrollToLatest")}
            onClick={() => scrollToBottom("smooth")}
            size="small"
            sx={{
              "position": "absolute",
              "top": 0,
              "left": "50%",
              "transform": "translate(-50%, -100%)",
              "bgcolor": "background.paper",
              "border": "1px solid",
              "borderColor": "divider",
              "boxShadow": 1,
              "&:hover": { bgcolor: theme => theme.palette.grey[100] },
            }}
          >
            <ArrowDownIcon size={16} />
          </IconButton>
        )}
        <Stack sx={{ gap: 1 }}>
          {error !== null && !isWorking && (
            <Stack direction="row" sx={{ gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <Typography color={isBudgetExhaustedError(error) ? "text.secondary" : "error"}>
                {isBudgetExhaustedError(error)
                  ? t("Assistant.Budget.Exhausted", { resetsAt: error.budget ? formatDate(error.budget.resetsAt) : "" })
                  : isConversationBusyError(error)
                    ? t("Assistant.Busy")
                    : isServiceUnavailableError(error)
                      ? t("Assistant.Unavailable")
                      : isMessageRejectedError(error)
                        ? t("Assistant.Rejected")
                        : t("Assistant.Failed")}
              </Typography>
              {failedText !== null && !isBudgetExhaustedError(error) && (
                <Button variant="outlined" size="small" onClick={() => send(failedText)}>
                  {t("Assistant.SendAgain")}
                </Button>
              )}
            </Stack>
          )}

          {budgetWarning && error === null && !isWorking && (
            <Typography sx={{ color: "text.secondary", fontSize: theme => theme.typography.pxToRem(12) }}>
              {t("Assistant.Budget.NearlySpent", {
                percent: String(budgetWarning.spentPercent),
                resetsAt: formatDate(budgetWarning.resetsAt),
              })}
            </Typography>
          )}

          {isEmpty && !assistantApi.isInvite && <AssistantExamples onPick={seedComposer} />}

          {closedMessage ? (
            <Typography sx={{ textAlign: "center", color: "text.secondary" }}>{closedMessage}</Typography>
          ) : (
            <AssistantComposer
              disabled={isWorking}
              onSend={send}
              seed={seed}
              isQuiet={showsDraftCard}
              placeholder={
                showsDraftCard
                  ? t("Assistant.Composer.PlaceholderChange")
                  : showsChoices
                    ? t("Assistant.Composer.PlaceholderCard")
                    : undefined
              }
            />
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

const ChatLine = ({ status, children }: { status?: ReactNode; children: ReactNode }) => (
  <Stack sx={{ alignSelf: "center", alignItems: "center", gap: 0.75 }}>
    {status}
    <Typography component="div" sx={{ fontSize: theme => theme.typography.pxToRem(12), color: "text.secondary" }}>
      {children}
    </Typography>
  </Stack>
);

const AssistantSide = ({ children }: { children: ReactNode }) => <Box>{children}</Box>;

const MessageBubble = ({
  role,
  text,
  isStreaming,
}: {
  role: "user" | "assistant";
  text: string;
  isStreaming?: boolean;
}) =>
  role === "user" ? (
    <Box
      sx={{
        alignSelf: "flex-end",
        maxWidth: "min(450px, 80%)",
        px: 2,
        py: 1,
        borderRadius: 2,
        bgcolor: "action.hover",
      }}
    >
      <AssistantMessageText text={text} />
    </Box>
  ) : (
    <Box aria-busy={isStreaming || undefined} sx={{ maxWidth: MESSAGE_WIDTH, minWidth: 0 }}>
      <AssistantMessageText text={text} isStreaming={isStreaming} />
    </Box>
  );
